import { useContext, useMemo } from "react";
import { UserContext } from "contexts/UserContext";
import type { Environment } from "components/Environment/utils/types";
import {
  getIngressApplicationsFromEnvironment,
  getEnvironmentStatus,
} from "components/Environment/utils/environtmentUtils";
import { getCurrentUserAccessIdentities } from "components/Environment/utils/secureLinkAccess";
import { WorkspaceStatus } from "models/Workspace.model";
import type { ApplicationListItem } from "../ApplicationTableRow";
import { userCanAccessApplicationPolicy } from "../applicationAccess";

export function useApplicationGroups(
  environments: Environment[],
  search: string,
) {
  const { me } = useContext(UserContext);
  const activeUserIdentities = useMemo(
    () => getCurrentUserAccessIdentities(me),
    [me?.email, me?.externalAuthId, me?.externalIdentities],
  );

  return useMemo(() => {
    const query = search.trim().toLowerCase();
    const applications = environments
      .flatMap((environment) =>
        getIngressApplicationsFromEnvironment(environment).map(
          (application): ApplicationListItem => ({ application, environment }),
        ),
      )
      .filter(
        ({ application, environment }) =>
          !query ||
          application.name?.toLowerCase().includes(query) ||
          application.hostname?.toLowerCase().includes(query) ||
          environment.name?.toLowerCase().includes(query),
      );
    const groups = applications.reduce(
      (result, item) => {
        const canAccess = userCanAccessApplicationPolicy(
          item.application,
          activeUserIdentities,
        );
        const state = !canAccess
          ? "inaccessible"
          : getEnvironmentStatus(item.environment) === WorkspaceStatus.Running
            ? "active"
            : "inactive";
        result[state].push(item);
        return result;
      },
      {
        active: [] as ApplicationListItem[],
        inaccessible: [] as ApplicationListItem[],
        inactive: [] as ApplicationListItem[],
      },
    );

    return { all: applications, ...groups };
  }, [activeUserIdentities, environments, search]);
}
